import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { PageHeader } from "@/components/Layout";
import { Empty, FormDialog, ListCard } from "@/components/Crud";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2 } from "lucide-react";

export default function Utenti() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/users").then(({ data }) => setRows(data));
  useEffect(() => {
    load();
    api.get("/customers").then(({ data }) => setCustomers(data));
  }, []);

  const fields = [
    { name: "name", label: "Nome", required: true },
    { name: "email", label: "Email", type: "email", required: !editing },
    { name: "password", label: editing ? "Nuova password (opzionale)" : "Password", required: !editing },
    {
      name: "role", label: "Ruolo", type: "select",
      options: [
        { value: "azienda", label: "Azienda (accesso totale)" },
        { value: "agente", label: "Agente" },
        { value: "tecnico", label: "Tecnico" },
        { value: "cliente", label: "Cliente (portale)" },
      ],
    },
    { name: "phone", label: "Telefono" },
    { name: "specialization", label: "Specializzazione (tecnico)" },
    { name: "default_discount_pct", label: "Sconto standard agente %", type: "number" },
    { name: "base_address", label: "Base di partenza (tecnico)", full: true },
    { name: "base_lat", label: "Base latitudine", type: "number" },
    { name: "base_lng", label: "Base longitudine", type: "number" },
    {
      name: "customer_id", label: "Cliente collegato (ruolo cliente)", type: "select", full: true,
      options: customers.map((c) => ({ value: c.id, label: c.name })),
    },
  ];

  const save = async (values) => {
    try {
      if (editing) await api.put(`/users/${editing.id}`, values);
      else await api.post("/users", values);
      await load();
    } catch (e) {
      throw new Error(apiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="utenti-page">
      <PageHeader title="Utenti e tecnici" subtitle={`${rows.length} account`}>
        <Button className="h-11 rounded-sm" onClick={() => { setEditing(null); setOpen(true); }} data-testid="new-user-btn">
          <Plus className="h-4 w-4 mr-2" /> Nuovo utente
        </Button>
      </PageHeader>

      <ListCard testid="utenti-list">
        {rows.map((u) => (
          <div key={u.id} className="px-4 sm:px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <p className="font-semibold">{u.name}</p>
                <Badge className="rounded-sm capitalize" variant={u.role === "azienda" ? "default" : "secondary"}>
                  {u.role}
                </Badge>
              </div>
              <p className="text-xs text-muted-foreground mt-1 font-mono">{u.email}</p>
              {u.base_address && <p className="text-xs text-muted-foreground">Base: {u.base_address}</p>}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="rounded-sm"
                      onClick={() => { setEditing(u); setOpen(true); }} data-testid={`edit-user-${u.id}`}>
                <Pencil className="h-3.5 w-3.5" />
              </Button>
              <Button variant="outline" size="sm" className="rounded-sm text-destructive"
                      onClick={async () => { await api.delete(`/users/${u.id}`); load(); }}
                      data-testid={`delete-user-${u.id}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ))}
        {rows.length === 0 && <Empty text="Nessun utente." />}
      </ListCard>

      <FormDialog open={open} onOpenChange={setOpen} title={editing ? "Modifica utente" : "Nuovo utente"}
                  fields={fields} initial={editing || { role: "tecnico" }} onSubmit={save} testid="user-dialog" />
    </div>
  );
}
